// All data derivation functions for the Analytics page.
// Input: normalized query/version records. No backend calls here.

// Matches actual API response shape from listQueries:
// - attributes are at item.attributes ?? item (root)
// - timestamp is epoch ms in `timestamp` field (not created_at)
// - status values: COMPLETED | FAILED | RUNNING | IN_PROGRESS | QUEUED | CANCELLED | TIMED_OUT
// - long_running is a boolean flag (not computed from execution_time)
// - version_id is tagged onto each query after fetching (not in response body)
export interface NormalizedQuery {
  query_id: string;
  query_string: string;
  status: string;         // COMPLETED | FAILED | RUNNING | IN_PROGRESS | QUEUED | CANCELLED | TIMED_OUT
  timestamp: number;      // epoch ms
  long_running: boolean;
  version_id: string;     // tagged at fetch time
  instance_id?: string;
  user_id?: string;       // present when include_user_id=true
}

// Matches actual API response shape from listVersions:
// - version_id, published, gds_enabled are at root OR item.attributes
export interface NormalizedVersion {
  version_id: string;
  published: boolean;
  gds_enabled: boolean;
  created_at?: number;  // epoch ms or undefined
}

export interface AnalyticsMetrics {
  total: number;
  completed: number;
  failed: number;
  timedOut: number;
  running: number;      // RUNNING | IN_PROGRESS | QUEUED
  longRunning: number;
  versionsQueried: number;
  totalVersions: number;
  lastActivityMs: number | null;
  daysActive: number;
}

export interface VolumeSeries {
  label: string;
  color: string;
  data: number[];
}

export interface VolumeData {
  dateLabels: string[];
  series: VolumeSeries[];
  maxValue: number;
}

export interface HorizBarItem {
  label: string;
  count: number;
  pct: number;   // 0-100
  color: string;
}

export interface HeatmapCellUser {
  id: string;
  count: number;
}

export interface HeatmapData {
  matrix: number[][];   // [7 weekdays Mon=0..Sun=6][12 two-hour buckets]
  users: HeatmapCellUser[][][];  // [7][12] — top users per cell
  max: number;
}

export interface VersionActivity {
  versionId: string;
  published: boolean;
  queryCount: number;
  pct: number;
}

const VERSION_COLORS = ['#8E42EE', '#F78E12', '#E8459B', '#22B8CF', '#3C5DE2', '#24CF35', '#A8927D'];

function isCompleted(status: string) { return status.toUpperCase() === 'COMPLETED'; }
function isFailed(status: string)    { const s = status.toUpperCase(); return s === 'FAILED' || s === 'ERROR'; }
function isTimedOut(status: string)  { return status.toUpperCase() === 'TIMED_OUT'; }
function isRunning(status: string)   {
  const s = status.toUpperCase();
  return s === 'RUNNING' || s === 'IN_PROGRESS' || s === 'QUEUED';
}

export function deriveMetrics(
  queries: NormalizedQuery[],
  versions: NormalizedVersion[],
): AnalyticsMetrics {
  const total      = queries.length;
  const completed  = queries.filter(q => isCompleted(q.status)).length;
  const failed     = queries.filter(q => isFailed(q.status)).length;
  const timedOut   = queries.filter(q => isTimedOut(q.status)).length;
  const running    = queries.filter(q => isRunning(q.status)).length;
  const longRunning = queries.filter(q => q.long_running).length;

  const versionIdsInQueries = new Set(queries.map(q => q.version_id).filter(Boolean));
  const versionsQueried = versionIdsInQueries.size;

  const timestamps = queries.map(q => q.timestamp).filter(t => t > 0);
  const lastActivityMs = timestamps.length > 0 ? Math.max(...timestamps) : null;

  const daySet = new Set(
    timestamps.map(t => {
      const d = new Date(t);
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    })
  );

  return {
    total, completed, failed, timedOut, running, longRunning,
    versionsQueried, totalVersions: versions.length,
    lastActivityMs, daysActive: daySet.size,
  };
}

export function deriveVolumeByDay(
  queries: NormalizedQuery[],
  startMs: number,
  endMs: number,
): VolumeData {
  const days: Date[] = [];
  const cur = new Date(startMs);
  cur.setUTCHours(0, 0, 0, 0);
  const end = new Date(endMs);
  end.setUTCHours(23, 59, 59, 999);
  while (cur <= end) {
    days.push(new Date(cur));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  const dateLabels = days.map(d => {
    const mo = d.getUTCMonth() + 1;
    const da = d.getUTCDate();
    return `${mo}/${da}`;
  });

  const activeVersionIds = Array.from(
    new Set(queries.map(q => q.version_id).filter(Boolean))
  );

  const series: VolumeSeries[] = activeVersionIds.slice(0, 7).map((vid, i) => {
    const data = days.map(day => {
      const dayStr = day.toISOString().slice(0, 10);
      return queries.filter(q => {
        const qDay = new Date(q.timestamp).toISOString().slice(0, 10);
        return q.version_id === vid && qDay === dayStr;
      }).length;
    });
    return { label: `v${vid}`, color: VERSION_COLORS[i % VERSION_COLORS.length], data };
  });

  if (series.length === 0) {
    series.push({ label: 'no data', color: '#E5DFEE', data: days.map(() => 0) });
  }

  const maxValue = Math.max(
    1,
    ...days.map((_, di) => series.reduce((sum, s) => sum + s.data[di], 0))
  );

  return { dateLabels, series, maxValue };
}

export function deriveOperationMix(queries: NormalizedQuery[]): HorizBarItem[] {
  const counts: Record<string, number> = {};
  for (const q of queries) {
    const text = (q.query_string ?? '').trimStart();
    const firstWord = text.split(/\s+/)[0].toUpperCase();
    const key = firstWord === 'CALL' && text.toUpperCase().includes('GDS.')
      ? 'CALL gds.*'
      : firstWord || 'OTHER';
    counts[key] = (counts[key] ?? 0) + 1;
  }
  const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
  const palette = ['#8E42EE', '#A96DEC', '#CBA8F3', '#E8DAFB', '#C9A0F5', '#D4B8F7', '#E5DFEE'];
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([label, count], i) => ({
      label,
      count,
      pct: Math.round((count / total) * 100),
      color: palette[i % palette.length],
    }));
}

export function deriveGDSUsage(queries: NormalizedQuery[]): HorizBarItem[] {
  const counts: Record<string, number> = {};
  for (const q of queries) {
    const text = q.query_string ?? '';
    if (!text.toUpperCase().includes('GDS.')) continue;
    const m = text.match(/CALL\s+GDS\.([A-Z_a-z.]+)/i);
    if (m) {
      const proc = m[1].toLowerCase();
      counts[proc] = (counts[proc] ?? 0) + 1;
    }
  }
  const palette = ['#8E42EE', '#F78E12', '#E8459B', '#22B8CF', '#3C5DE2', '#A8927D', '#24CF35', '#F5A623', '#9B59B6', '#E74C3C'];
  const max = Math.max(1, ...Object.values(counts));
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([label, count], i) => ({
      label,
      count,
      pct: Math.round((count / max) * 100),
      color: palette[i % palette.length],
    }));
}

export function deriveGraphDemand(queries: NormalizedQuery[]): { nodes: HorizBarItem[]; rels: HorizBarItem[] } {
  const nodeCounts: Record<string, number> = {};
  const relCounts: Record<string, number> = {};

  for (const q of queries) {
    const text = q.query_string ?? '';

    // Relationship types: [...:REL_TYPE...] — must be inside square brackets
    const relMatches = new Set<string>();
    Array.from(text.matchAll(/\[[^\]]*:([A-Z_][A-Z0-9_|]*)[^\]]*\]/g)).forEach(m => {
      // handle multi-label rels like [:TYPE_A|TYPE_B]
      m[1].split('|').forEach(t => {
        const label = t.trim();
        if (label) {
          relCounts[label] = (relCounts[label] ?? 0) + 1;
          relMatches.add(label);
        }
      });
    });

    // Node labels: (n:Label) — colon inside parens, NOT inside brackets
    // Strip relationship brackets first to avoid double-counting
    const strippedText = text.replace(/\[[^\]]*\]/g, '[]');
    Array.from(strippedText.matchAll(/:([A-Za-z_][A-Za-z0-9_]*)/g)).forEach(m => {
      const label = m[1];
      // Skip if it looks like a relationship type (all caps + underscores)
      if (/^[A-Z][A-Z0-9_]*$/.test(label) && relMatches.has(label)) return;
      nodeCounts[label] = (nodeCounts[label] ?? 0) + 1;
    });
  }

  const nodePalette = ['#8E42EE', '#A96DEC', '#CBA8F3', '#E8DAFB', '#7433CC', '#9B59B6', '#C39BD3', '#D2B4DE', '#E8DAEF'];
  const relPalette  = ['#22B8CF', '#A5DDE6', '#7ECCD9', '#C5ECF1', '#1A9BB0', '#48C9B0', '#76D7C4', '#A2D9CE'];
  const nodeMax = Math.max(1, ...Object.values(nodeCounts));
  const relMax  = Math.max(1, ...Object.values(relCounts));

  const nodes = Object.entries(nodeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 9)
    .map(([label, count], i) => ({
      label: `:${label}`, count,
      pct: Math.round((count / nodeMax) * 100),
      color: nodePalette[i % nodePalette.length],
    }));

  const rels = Object.entries(relCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([label, count], i) => ({
      label, count,
      pct: Math.round((count / relMax) * 100),
      color: relPalette[i % relPalette.length],
    }));

  return { nodes, rels };
}

export function deriveHeatmap(queries: NormalizedQuery[]): HeatmapData {
  // matrix[weekday 0=Mon..6=Sun][bucket 0=00h..11=22h, 2-hr slots]
  const matrix: number[][] = Array.from({ length: 7 }, () => Array(12).fill(0));
  // userCounts[row][bucket][userId] = count
  const userCounts: Record<string, number>[][] = Array.from({ length: 7 }, () =>
    Array.from({ length: 12 }, () => ({}))
  );
  for (const q of queries) {
    if (!q.timestamp) continue;
    const d = new Date(q.timestamp);
    const jsDay = d.getDay(); // 0=Sun
    const row = jsDay === 0 ? 6 : jsDay - 1;
    const bucket = Math.floor(d.getHours() / 2);
    matrix[row][bucket]++;
    const uid = q.user_id ?? q.instance_id ?? 'unknown';
    userCounts[row][bucket][uid] = (userCounts[row][bucket][uid] ?? 0) + 1;
  }
  const users: HeatmapCellUser[][][] = userCounts.map(row =>
    row.map(cell =>
      Object.entries(cell)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([id, count]) => ({ id, count }))
    )
  );
  const max = Math.max(1, ...matrix.flat());
  return { matrix, users, max };
}

export function deriveVersionActivity(
  queries: NormalizedQuery[],
  versions: NormalizedVersion[],
): VersionActivity[] {
  const counts: Record<string, number> = {};
  for (const q of queries) {
    if (q.version_id) counts[q.version_id] = (counts[q.version_id] ?? 0) + 1;
  }
  const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
  return versions
    .map(v => ({
      versionId: v.version_id,
      published: v.published,
      queryCount: counts[v.version_id] ?? 0,
      pct: Math.round(((counts[v.version_id] ?? 0) / total) * 100),
    }))
    .sort((a, b) => b.queryCount - a.queryCount);
}

// ── "Who is using it" derivation ─────────────────────────────────────────

export interface UserRow {
  id: string;            // instance_id value used as user identity
  isService: boolean;    // heuristic: no '@' → service account
  runs: number;
  longRunning: number;
  versions: string[];    // distinct version_ids used
  lastSeenMs: number;
  sharePct: number;      // 0-100, share of total runs
}

export interface UserMetrics {
  totalUsers: number;
  serviceAccounts: number;
  activeUsers: number;       // users with ≥1 query in period
  newUsers: number;          // users whose first query falls in this period (vs all time)
  retained: number;          // users that also appear in prior period
  retainedPct: number;
  medianRunsPerUser: number;
  p90RunsPerUser: number;
  top3Pct: number;           // share held by top-3 users (key-person risk)
  humanRuns: number;
  serviceRuns: number;
  humanLongRunPct: number;   // % of human runs that are long-running
  serviceLongRunPct: number;
}

export interface AdoptionDay {
  label: string;       // "8/25"
  returning: number;
  newUsers: number;
}

// Is an instance_id a service account? Heuristic: no '@' in the id.
function isServiceId(id: string): boolean {
  return !id.includes('@');
}

export function deriveUserRows(queries: NormalizedQuery[], totalRuns: number): UserRow[] {
  const map = new Map<string, { runs: number; lr: number; versions: Set<string>; lastMs: number }>();

  for (const q of queries) {
    // Prefer user_id (returned when include_user_id=true) over instance_id fallback
    const id = q.user_id ?? q.instance_id ?? 'unknown';
    const entry = map.get(id) ?? { runs: 0, lr: 0, versions: new Set(), lastMs: 0 };
    entry.runs++;
    if (q.long_running) entry.lr++;
    if (q.version_id) entry.versions.add(q.version_id);
    if (q.timestamp > entry.lastMs) entry.lastMs = q.timestamp;
    map.set(id, entry);
  }

  const total = totalRuns || 1;
  return Array.from(map.entries())
    .map(([id, e]) => ({
      id,
      isService: isServiceId(id),
      runs: e.runs,
      longRunning: e.lr,
      versions: Array.from(e.versions).sort((a, b) => Number(a) - Number(b)),
      lastSeenMs: e.lastMs,
      sharePct: Math.round((e.runs / total) * 100),
    }))
    .sort((a, b) => b.runs - a.runs);
}

export function deriveUserMetrics(
  queriesInPeriod: NormalizedQuery[],
  allQueries: NormalizedQuery[],    // unfiltered by period — for "new" detection
  startMs: number,
): UserMetrics {
  const rows = deriveUserRows(queriesInPeriod, queriesInPeriod.length);

  const totalUsers   = rows.length;
  const serviceAccounts = rows.filter(r => r.isService).length;
  const activeUsers  = totalUsers;

  // "New" = their very first query across all time falls within this period
  const firstSeenMap = new Map<string, number>();
  for (const q of allQueries) {
    const id = q.user_id ?? q.instance_id ?? 'unknown';
    const prev = firstSeenMap.get(id);
    if (prev === undefined || q.timestamp < prev) firstSeenMap.set(id, q.timestamp);
  }
  const newUsers = rows.filter(r => {
    const first = firstSeenMap.get(r.id);
    return first !== undefined && first >= startMs;
  }).length;

  const retained = activeUsers - newUsers;
  const retainedPct = activeUsers > 0 ? Math.round((retained / activeUsers) * 100) : 0;

  const runCounts = rows.map(r => r.runs).sort((a, b) => a - b);
  const medianRunsPerUser = runCounts.length > 0
    ? runCounts[Math.floor(runCounts.length / 2)]
    : 0;
  const p90RunsPerUser = runCounts.length > 0
    ? runCounts[Math.floor(runCounts.length * 0.9)]
    : 0;

  const top3Runs = rows.slice(0, 3).reduce((s, r) => s + r.runs, 0);
  const top3Pct = queriesInPeriod.length > 0
    ? Math.round((top3Runs / queriesInPeriod.length) * 100)
    : 0;

  const humanRows   = rows.filter(r => !r.isService);
  const serviceRows = rows.filter(r => r.isService);
  const humanRuns   = humanRows.reduce((s, r) => s + r.runs, 0);
  const serviceRuns = serviceRows.reduce((s, r) => s + r.runs, 0);
  const humanLR     = humanRows.reduce((s, r) => s + r.longRunning, 0);
  const serviceLR   = serviceRows.reduce((s, r) => s + r.longRunning, 0);

  return {
    totalUsers, serviceAccounts, activeUsers, newUsers,
    retained, retainedPct,
    medianRunsPerUser, p90RunsPerUser, top3Pct,
    humanRuns, serviceRuns,
    humanLongRunPct: humanRuns > 0 ? Math.round((humanLR / humanRuns) * 100) : 0,
    serviceLongRunPct: serviceRuns > 0 ? Math.round((serviceLR / serviceRuns) * 100) : 0,
  };
}

export function deriveAdoption(
  queriesInPeriod: NormalizedQuery[],
  allQueries: NormalizedQuery[],
  startMs: number,
  endMs: number,
): AdoptionDay[] {
  // Build first-seen map across all time
  const firstSeenMap = new Map<string, number>();
  for (const q of allQueries) {
    const id = q.user_id ?? q.instance_id ?? 'unknown';
    const prev = firstSeenMap.get(id);
    if (prev === undefined || q.timestamp < prev) firstSeenMap.set(id, q.timestamp);
  }

  const days: Date[] = [];
  const cur = new Date(startMs);
  cur.setUTCHours(0, 0, 0, 0);
  while (cur.getTime() <= endMs) {
    days.push(new Date(cur));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }

  return days.map(day => {
    const dayStr = day.toISOString().slice(0, 10);
    const dayStart = day.getTime();
    const dayEnd   = dayStart + 86_400_000 - 1;

    // distinct users active that day
    const activeIds = new Set(
      queriesInPeriod
        .filter(q => q.timestamp >= dayStart && q.timestamp <= dayEnd)
        .map(q => q.user_id ?? q.instance_id ?? 'unknown')
    );

    let newU = 0, returningU = 0;
    Array.from(activeIds).forEach(id => {
      const first = firstSeenMap.get(id);
      if (first !== undefined && new Date(first).toISOString().slice(0, 10) === dayStr) {
        newU++;
      } else {
        returningU++;
      }
    });

    const mo = day.getUTCMonth() + 1;
    const da = day.getUTCDate();
    return { label: `${mo}/${da}`, returning: returningU, newUsers: newU };
  });
}

export function formatLastActivity(ms: number | null): string {
  if (!ms) return 'Never';
  const diffMs = Date.now() - ms;
  const m = Math.floor(diffMs / 60_000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export function formatLastActivityUnit(ms: number | null): string {
  if (!ms) return '';
  const diffMs = Date.now() - ms;
  const m = Math.floor(diffMs / 60_000);
  if (m < 1) return '';
  if (m < 60) return 'm ago';
  const h = Math.floor(m / 60);
  if (h < 24) return ' ago';
  return ' ago';
}
